import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Gift, Share2, CheckCircle, User, Mail, ArrowRight, 
  Smartphone, Sparkles, Heart
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import './ClaimGift.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_LINK = "https://play.google.com/store/apps/details?id=com.customers.bhyyve";
const POLLEN_IMG = "https://customer-assets.emergentagent.com/job_bee-booking/artifacts/k8zgjyqb_Pollen%20-%20bHyyve%20Mascot.png";

const ClaimGift = () => {
  const [searchParams] = useSearchParams();
  const [giftData, setGiftData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareForm, setShowShareForm] = useState(false);
  const [shareForm, setShareForm] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [isSharing, setIsSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [isFromShare, setIsFromShare] = useState(false);

  useEffect(() => {
    const giftId = searchParams.get('id');
    const code = searchParams.get('code');
    const name = searchParams.get('name');
    const desc = searchParams.get('desc');
    const fromShare = searchParams.get('shared') === 'true';
    
    setIsFromShare(fromShare);
    
    if (code && name) {
      // Gift data from URL params (shared link)
      setGiftData({
        id: giftId || 'shared',
        name: decodeURIComponent(name),
        description: desc ? decodeURIComponent(desc) : '',
        code: decodeURIComponent(code)
      });
      setIsLoading(false);
    } else if (giftId) {
      // Fetch gift data from backend
      fetchGiftData(giftId);
    } else {
      setIsLoading(false);
    }
  }, [searchParams]);

  const fetchGiftData = async (giftId) => {
    try {
      const response = await fetch(`${API_URL}/api/gift/${giftId}`);
      if (response.ok) {
        const data = await response.json();
        setGiftData(data);
      }
    } catch (error) {
      console.error('Error fetching gift:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareFormChange = (field, value) => {
    setShareForm(prev => ({ ...prev, [field]: value }));
  };

  const handleShareGift = async (e) => {
    e.preventDefault();
    
    if (!shareForm.firstName || !shareForm.lastName || !shareForm.email) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shareForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSharing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/gift/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalGiftId: giftData.id,
          gift: {
            name: giftData.name,
            description: giftData.description,
            code: giftData.code
          },
          friend: {
            firstName: shareForm.firstName,
            lastName: shareForm.lastName,
            email: shareForm.email
          }
        })
      });
      
      if (response.ok) {
        setShareSuccess(true);
        toast.success('Gift shared successfully!');
      } else {
        throw new Error('Failed to share');
      }
    } catch (error) {
      console.error('Error sharing gift:', error);
      toast.error('Failed to share gift. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const copyCode = () => {
    if (giftData?.code) {
      navigator.clipboard.writeText(giftData.code);
      toast.success('Gift code copied!');
    }
  };

  if (isLoading) {
    return (
      <div className="cgift-page cgift-loading">
        <div className="cgift-spinner"></div>
        <p>Loading your gift...</p>
      </div>
    );
  }

  if (!giftData) {
    return (
      <div className="cgift-page cgift-error">
        <Gift size={64} />
        <h1>Gift Not Found</h1>
        <p>This gift link may be invalid or expired.</p>
        <a href="/" className="cgift-home-link">Go to Homepage</a>
      </div>
    );
  }

  // Share Success Screen
  if (shareSuccess) {
    return (
      <div className="cgift-page" data-testid="share-success">
        <div className="cgift-container">
          <div className="cgift-success-card">
            <div className="cgift-success-icon">
              <CheckCircle size={64} />
            </div>
            <h1>Gift Shared!</h1>
            <p>
              Your friend <strong>{shareForm.firstName}</strong> will receive an email with their gift at <strong>{shareForm.email}</strong>
            </p>
            <div className="cgift-success-message">
              <Heart size={20} />
              <span>Thank you for spreading the joy!</span>
            </div>
            <a href="/" className="cgift-home-btn">
              Visit bHyyve Homepage
            </a>
          </div>
          <img src={POLLEN_IMG} alt="Pollen" className="cgift-success-bee" />
        </div>
      </div>
    );
  }

  return (
    <div className="cgift-page" data-testid="claim-gift-page">
      {/* Header */}
      <header className="cgift-header">
        <a href="/" className="cgift-logo-link">
          <img src="/logo.svg" alt="bHyyve" className="cgift-logo" />
          <span className="cgift-logo-text">bHyyve</span>
        </a>
      </header>

      <div className="cgift-content">
        {/* Gift Display */}
        <div className="cgift-main">
          <div className="cgift-badge">
            <Sparkles size={18} />
            <span>{isFromShare ? 'A Friend Sent You a Gift!' : 'You\'ve Received a Gift!'}</span>
          </div>
          
          <div className="cgift-card">
            <div className="cgift-card-header">
              <Gift size={32} />
              <h1>{giftData.name}</h1>
            </div>
            {giftData.description && (
              <p className="cgift-description">{giftData.description}</p>
            )}
            <div className="cgift-code-box">
              <span className="cgift-code-label">Your Gift Code</span>
              <div className="cgift-code-display">
                <span className="cgift-code">{giftData.code}</span>
                <Button onClick={copyCode} className="cgift-copy-btn">
                  Copy
                </Button>
              </div>
            </div>
            <p className="cgift-instructions">
              Use this code at checkout in the bHyyve app to redeem your gift!
            </p>
          </div>

          {/* App Download CTA */}
          <div className="cgift-app-cta">
            <div className="cgift-app-content">
              <Smartphone size={28} />
              <div>
                <h3>Don't have the bHyyve app?</h3>
                <p>Download now to redeem your gift and discover amazing local services!</p>
              </div>
            </div>
            <a href={APP_LINK} target="_blank" rel="noopener noreferrer" className="cgift-download-btn">
              Download App
              <ArrowRight size={18} />
            </a>
          </div>

          {/* Share Section (only for original recipients, not shared ones) */}
          {!isFromShare && (
            <div className="cgift-share-section">
              {!showShareForm ? (
                <div className="cgift-share-prompt">
                  <div className="cgift-share-text">
                    <Share2 size={24} />
                    <div>
                      <h3>Share the Love!</h3>
                      <p>Pass this gift to a friend or family member</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setShowShareForm(true)}
                    className="cgift-share-btn"
                    data-testid="show-share-form-btn"
                  >
                    Share Gift
                  </Button>
                </div>
              ) : (
                <div className="cgift-share-form-container">
                  <h3>Share with a Friend</h3>
                  <p>Enter your friend's details to send them this gift</p>
                  <form onSubmit={handleShareGift} className="cgift-share-form" data-testid="share-gift-form">
                    <div className="cgift-form-row">
                      <div className="cgift-field">
                        <label>First Name *</label>
                        <Input
                          type="text"
                          value={shareForm.firstName}
                          onChange={(e) => handleShareFormChange('firstName', e.target.value)}
                          placeholder="Friend's first name"
                          required
                          data-testid="friend-first-name"
                        />
                      </div>
                      <div className="cgift-field">
                        <label>Last Name *</label>
                        <Input
                          type="text"
                          value={shareForm.lastName}
                          onChange={(e) => handleShareFormChange('lastName', e.target.value)}
                          placeholder="Friend's last name"
                          required
                          data-testid="friend-last-name"
                        />
                      </div>
                    </div>
                    <div className="cgift-field">
                      <label>Email Address *</label>
                      <Input
                        type="email"
                        value={shareForm.email}
                        onChange={(e) => handleShareFormChange('email', e.target.value)}
                        placeholder="friend@email.com"
                        required
                        data-testid="friend-email"
                      />
                    </div>
                    <div className="cgift-form-actions">
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => setShowShareForm(false)}
                        className="cgift-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit"
                        className="cgift-submit-btn"
                        disabled={isSharing}
                        data-testid="submit-share-btn"
                      >
                        {isSharing ? 'Sending...' : (
                          <>
                            <Mail size={18} />
                            Send Gift
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pollen Mascot */}
        <div className="cgift-pollen-container">
          <img src={POLLEN_IMG} alt="Pollen - bHyyve Mascot" className="cgift-pollen" />
          <div className="cgift-pollen-bubble">
            {isFromShare 
              ? "A friend thought of you! Enjoy your gift! 🎁" 
              : "Congrats on your gift! Share the joy with someone special! 🐝"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimGift;
